/**
 * TIFF decoder via geotiff.js: 8/16/32-bit integer and 16/32/64-bit float,
 * grey / RGB (+alpha), any compression geotiff supports (LZW, Deflate,
 * PackBits, JPEG, ...). Palette and YCbCr images are converted to 8-bit RGB.
 */
import { fromArrayBuffer } from 'geotiff';
import type { EncodingHint, PixelArray, RawFrame } from '../../core/types';
import { applyOrientation, defaultHint, makeFrame, toFloat32 } from '../frame';
import { iccToHint, parseIcc } from '../icc';

export async function decodeTiff(bytes: Uint8Array, name: string): Promise<RawFrame> {
  const buf = bytes.buffer.slice(bytes.byteOffset, bytes.byteOffset + bytes.byteLength) as ArrayBuffer;
  const tiff = await fromArrayBuffer(buf);
  const count = await tiff.getImageCount();
  const img = await tiff.getImage(0);
  const fd = img.fileDirectory;
  const width = img.getWidth();
  const height = img.getHeight();
  const spp = img.getSamplesPerPixel();
  const bits = img.getBitsPerSample(0) as number;
  const fmt = (img.getSampleFormat(0) as number) ?? 1; // 1 uint, 2 int, 3 float
  const load = async (tag: number | string) => {
    try {
      return fd.hasTag(tag as never) ? await fd.loadValue(tag as never) : undefined;
    } catch {
      return undefined;
    }
  };
  const photometric = (await load('PhotometricInterpretation')) as number | undefined;
  const warnings: string[] = [];
  const info: Record<string, string> = {
    'Bits per sample': `${bits}`,
    'Sample format': ['', 'unsigned int', 'signed int', 'float'][fmt] ?? `${fmt}`,
    'Samples per pixel': `${spp}`,
    Photometric: `${photometric ?? '?'}`,
  };
  if (count > 1) {
    info.Pages = `${count}`;
    warnings.push(`Multi-page TIFF: only the first of ${count} images is loaded.`);
  }

  let hint: EncodingHint | null = null;
  const icc = (await load(34675)) as unknown;
  if (icc && (icc as ArrayLike<number>).length) {
    const parsed = parseIcc(icc instanceof Uint8Array ? icc : Uint8Array.from(icc as ArrayLike<number>));
    if (parsed) {
      hint = iccToHint(parsed);
      info['ICC profile'] = parsed.description;
    }
  }

  let data: PixelArray;
  let channels = spp;
  let maxCode = 1;
  let isFloat = false;
  let bitDepth = bits;
  if (photometric === 3 || photometric === 6 || photometric === 5 || photometric === 8) {
    if (photometric === 5 || photometric === 8) throw new Error('CMYK and CIELab TIFFs are not supported');
    // Palette / YCbCr: let geotiff convert to 8-bit RGB.
    const rgb = (await img.readRGB({ interleave: true })) as unknown as ArrayLike<number>;
    data = rgb instanceof Uint8Array ? rgb : Uint8Array.from(rgb);
    channels = 3;
    maxCode = photometric === 3 && bits === 16 ? 65535 : 255;
    if (photometric === 3 && bits === 16) data = Uint16Array.from(rgb);
    bitDepth = 8;
  } else {
    const r = (await img.readRasters({ interleave: true })) as unknown as ArrayLike<number> & { constructor: unknown };
    if (fmt === 3) {
      data = r instanceof Float32Array ? r : toFloat32(r);
      isFloat = true;
    } else if (fmt === 2) {
      // Signed integers: keep the numeric value, normalised by the positive range.
      data = toFloat32(r, 1 / (2 ** (bits - 1) - 1));
      isFloat = true;
      warnings.push('Signed integer samples converted to float (value / max positive code).');
    } else if (bits <= 8) {
      data = r instanceof Uint8Array ? r : Uint8Array.from(r);
      maxCode = 2 ** bits - 1;
    } else if (bits <= 16) {
      data = r instanceof Uint16Array ? r : Uint16Array.from(r);
      maxCode = 2 ** bits - 1;
    } else {
      data = toFloat32(r, 1 / (2 ** bits - 1));
      isFloat = true;
      warnings.push(`${bits}-bit unsigned integers normalised to float 0..1.`);
    }
    if (photometric === 0) {
      // WhiteIsZero: invert so that larger means brighter.
      if (isFloat) for (let i = 0; i < data.length; i++) data[i] = 1 - data[i];
      else for (let i = 0; i < data.length; i++) data[i] = maxCode - data[i];
      warnings.push('WhiteIsZero photometric interpretation inverted.');
    }
  }
  if (!hint) {
    if (isFloat) hint = defaultHint('Float TIFF without ICC profile: assuming linear light', 'linear');
    else if (channels <= 2 && bitDepth > 8)
      hint = defaultHint('High-bit greyscale TIFF without ICC profile: assuming linear (scientific camera convention)', 'linear');
    else hint = defaultHint('No ICC profile: assuming sRGB');
  }
  let pixelsPerMm: number | undefined;
  const xres = (await load('XResolution')) as number | ArrayLike<number> | undefined;
  const unit = (await load('ResolutionUnit')) as number | undefined;
  // Rationals may arrive as [numerator, denominator] (array or typed array) or as a number.
  const xr = typeof xres === 'number' ? xres : xres && xres.length >= 2 ? xres[0] / (xres[1] || 1) : xres?.[0];
  if (xr && (unit === 2 || unit === 3)) pixelsPerMm = unit === 2 ? xr / 25.4 : xr / 10;
  const orientation = (await load('Orientation')) as number | undefined;
  if (orientation && orientation !== 1) {
    const o = applyOrientation(data, width, height, channels, orientation);
    data = o.data;
    info.Orientation = `${orientation} (applied)`;
    return makeFrame({
      width: o.width,
      height: o.height,
      data,
      channels,
      maxCode,
      isFloat,
      bitDepth,
      format: 'tiff',
      name,
      encoding: hint,
      info,
      warnings,
      pixelsPerMm,
    });
  }
  return makeFrame({
    width,
    height,
    data,
    channels,
    maxCode,
    isFloat,
    bitDepth,
    format: 'tiff',
    name,
    encoding: hint,
    info,
    warnings,
    pixelsPerMm,
  });
}
